import { CheckIcon, SubHeading, Tab, Tabs } from '@movingimage-evp/mi-ui-component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
  AssetUploadStatusCode,
  EventStage,
  State,
  useGetWebcastMediaQuery,
} from '../../../../../generated/graphql-manager';
import { getLanguageKey } from '../../../../../utils';
import { useUserPermissions } from '../../../../hooks/user-permissions';
import { CoverMediaUploader } from '../cover-media-uploader';
import { MediaAccordion } from '../media-accordion';

import styles from '../media.module.css';

const COVER_IMAGE_POLL_INTERVAL = 2000;

type Props = {
  eventStage: EventStage;
  onLoadStart: () => void;
  onLoadEnd: () => void;
};

export function MediaContent({ eventStage, onLoadStart, onLoadEnd }: Props) {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const { isEventEditingAllowed } = useUserPermissions();

  const { data, refetch, startPolling, stopPolling } = useGetWebcastMediaQuery({
    variables: { webcastId },
    skip: !webcastId,
    fetchPolicy: 'no-cache',
  });

  const webcast = data?.webcast;
  const archivedWebcast = webcast?.state === State.ARCHIVED;
  const primaryLanguage = webcast?.primaryLang;
  const additionalLanguages = webcast?.additionalLangs || [];
  const languages = [primaryLanguage, ...additionalLanguages]
    .filter((language) => language !== undefined)
    .filter((language) => language.__typename === 'StandardLanguage');

  const key = eventStage === EventStage.PRELIVE ? 'preLive' : 'postLive';

  const contents = webcast?.contents;
  const coverImagePending = contents?.some(
    (content) => content[key]?.coverImage?.status.code === AssetUploadStatusCode.PENDING
  );

  const formDisabled = !isEventEditingAllowed || archivedWebcast;

  useEffect(() => {
    if (!coverImagePending) stopPolling();
  }, [coverImagePending, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const hasContent = contents?.some(
    (content) => content[key]?.coverImage || content[key]?.video || content[key]?.videoVodManifestUrl
  );

  return (
    <div className={styles.wrapper}>
      <SubHeading>{t(`manager.webcastSetup.media.subHeading.${key}`)}</SubHeading>

      {primaryLanguage && languages.length === 1 && (
        <MediaAccordion testId={key} visible={!hasContent}>
          <CoverMediaUploader
            testId={key}
            language={primaryLanguage.code}
            eventStage={eventStage}
            coverImage={contents?.[0][key]?.coverImage}
            coverVideo={contents?.[0][key]?.video}
            videoVodManifestUrl={contents?.[0][key]?.videoVodManifestUrl}
            disabled={formDisabled}
            onLoadStart={onLoadStart}
            onLoadEnd={onLoadEnd}
            refetch={(options) => {
              if (options?.poll) {
                startPolling(COVER_IMAGE_POLL_INTERVAL);
              } else {
                refetch();
              }
            }}
          />
        </MediaAccordion>
      )}

      {languages.length > 1 && (
        <Tabs compact={languages.length <= 4} data-testid={`${key}-media-tabs`}>
          {languages.map((language) => {
            const translatedLanguage = t(
              `manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`
            );

            const label =
              language === primaryLanguage
                ? t('manager.webcastSetup.media.primaryLanguage', { primaryLanguage: translatedLanguage })
                : translatedLanguage;

            const content = contents?.find((content) => content.language === language.code);
            const data = content?.[key];
            const coverImagePending = data?.coverImage?.status.code === AssetUploadStatusCode.PENDING;
            const contentAvailable = Boolean(
              data?.coverImage?.assetId || data?.video?.videoId || data?.videoVodManifestUrl
            );
            const hideAccordion = contentAvailable && !coverImagePending;

            return (
              <Tab
                key={language.code}
                data-testid={`${key}-tab-${language.code}`}
                label={
                  <>
                    {label}
                    {contentAvailable && <CheckIcon className={styles.presentationExistsIcon} />}
                  </>
                }
              >
                <MediaAccordion
                  key={language.code}
                  testId={`${key}-${language.code}`}
                  visible={!hideAccordion}
                  className={styles.mediaAccordion}
                >
                  <CoverMediaUploader
                    key={language.code}
                    testId={`${key}-${language.code}`}
                    language={language.code}
                    eventStage={eventStage}
                    coverImage={data?.coverImage}
                    coverVideo={data?.video}
                    videoVodManifestUrl={data?.videoVodManifestUrl}
                    disabled={formDisabled}
                    onLoadStart={onLoadStart}
                    onLoadEnd={onLoadEnd}
                    refetch={(options) => {
                      if (options?.poll) {
                        startPolling(COVER_IMAGE_POLL_INTERVAL);
                      } else {
                        refetch();
                      }
                    }}
                  />
                </MediaAccordion>
              </Tab>
            );
          })}
        </Tabs>
      )}
    </div>
  );
}
